import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TextField,
} from '@mui/material';

import { Helmet } from 'react-helmet-async';

import { UseFormRegister } from 'react-hook-form';
import { useUserContext } from '../../../../contexts/UserContext';

import FormStyle from '../FormStyle';
import showTableData from '../Functions/showTableData';
import api from '../../../../services/api';
import ChangedWithSuccess from './ChangedWithSuccess';

interface UpdateUser {
  id: number;
  name: string;
  surname: string;
  nameKana: string;
  surnameKana: string;
  postCode: string;
  stateCity: string;
  streetBanNumber: number;
  buildingNumber: string;
  phone: string;
  email: string;
}

function ChangeForm(
  {
    addFormValues,
    handleSubmit,
    setIsEditing,
    errors,
    getUser,
  }: {
    addFormValues: UseFormRegister<UpdateUser>,
    handleSubmit: any,
    setIsEditing: (isEditing: boolean) => void,
    errors: any,
    getUser: () => Promise<void>,
  },
) {
  // variables
  const { user, updateUserContext } = useUserContext();
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // functions
  const handleEditConfirmation = handleSubmit(async (data: UpdateUser) => {
    setIsLoading(true);
    api.put(`/v1/users/${user.id}`, data)
      .then(async (response) => {
        updateUserContext(response.data.data);
        await getUser();
        setHasChanged(true);
      }).catch((error) => {
        setErrorMessage(error.response.data.error);
      }).finally(() => {
        setIsLoading(false);
      });
  });

  // render conditions
  if (hasChanged) {
    return (
      <ChangedWithSuccess
        setIsEditing={setIsEditing}
        setHasChanged={setHasChanged}
      />
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="90vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="main">
      <Helmet>
        <title>Change profile</title>
      </Helmet>
      <main
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 65px)',
        }}
      >
        <Box className="container-fluid my-1">
          <Box
            sx={FormStyle.previewHeader}
          >
            <h1 className="text-bold">Change profile</h1>
            Enter the profile information you want to change and click the &quot;Save&quot; button.
          </Box>
          <Box
            className="text-danger"
            sx={FormStyle.errorMessage}
          >
            <h4 className="text-bold">{errorMessage}</h4>
          </Box>
          <Box className="card">
            <Box className="card-body">
              <Grid
                container
                justifyContent="center"
                spacing={1}
              >
                <Grid item xs={12}>
                  <Table className="table-form-setting">
                    <TableBody>
                      {showTableData({
                        ticket: 'Name',
                        firstValueLabel: 'Last',
                        value: (
                          <TextField
                            {...addFormValues('surname')}
                            size="small"
                            helperText={errors.surname?.message}
                            error={!!errors.surname}
                            sx={{ width: '100%' }}
                            placeholder="Yamada"
                            inputProps={{ translate: 'no', className: 'notranslate' }}
                          />
                        ),
                        secondValueLabel: 'First',
                        secondValue: (
                          <TextField
                            {...addFormValues('name')}
                            size="small"
                            helperText={errors.name?.message}
                            error={!!errors.name}
                            sx={{ width: '100%' }}
                            placeholder="Taro"
                            inputProps={{ translate: 'no', className: 'notranslate' }}
                          />
                        ),
                        isRequired: true,
                      })}
                      {showTableData({
                        ticket: 'Name (furigana)',
                        firstValueLabel: 'Sei',
                        value: (
                          <TextField
                            {...addFormValues('surnameKana')}
                            size="small"
                            helperText={errors.surnameKana?.message}
                            error={!!errors.surnameKana}
                            sx={{ width: '100%' }}
                            placeholder="Yamada"
                            inputProps={{ translate: 'no', className: 'notranslate' }}
                          />
                        ),
                        secondValueLabel: 'Mei',
                        secondValue: (
                          <TextField
                            {...addFormValues('nameKana')}
                            size="small"
                            helperText={errors.nameKana?.message}
                            error={!!errors.nameKana}
                            sx={{ width: '100%' }}
                            placeholder="Taro"
                            inputProps={{ translate: 'no', className: 'notranslate' }}
                          />
                        ),
                      })}
                      {showTableData({
                        ticket: 'Post code',
                        value: (
                          <TextField
                            {...addFormValues('postCode')}
                            size="small"
                            helperText={errors.postCode?.message}
                            error={!!errors.postCode}
                            sx={{ width: '100%' }}
                            placeholder="1230001"
                            inputProps={{ translate: 'no', className: 'notranslate' }}
                          />
                        ),
                        extraText: 'Please enter without hyphens ("-").',
                      })}
                      {showTableData({
                        ticket: 'Prefecture/city/ward/town/village',
                        value: (
                          <TextField
                            {...addFormValues('stateCity')}
                            size="small"
                            helperText={errors.stateCity?.message}
                            error={!!errors.stateCity}
                            sx={{ width: '100%' }}
                            placeholder="e.g. Tokyo-to Shibuya-ku"
                            inputProps={{ translate: 'no', className: 'notranslate' }}
                          />
                        ),
                      })}
                      {showTableData({
                        ticket: 'Chome and number',
                        value: (
                          <TextField
                            {...addFormValues('streetBanNumber')}
                            size="small"
                            helperText={errors.streetBanNumber?.message}
                            error={!!errors.streetBanNumber}
                            sx={{ width: '100%' }}
                            placeholder="1-2-3"
                            inputProps={{ translate: 'no', className: 'notranslate' }}
                          />
                        ),
                      })}
                      {showTableData({
                        ticket: 'Building name and room number',
                        value: (
                          <TextField
                            {...addFormValues('buildingNumber')}
                            size="small"
                            helperText={errors.buildingNumber?.message}
                            error={!!errors.buildingNumber}
                            sx={{ width: '100%' }}
                            placeholder="Please enter if you have a building name or room number"
                            inputProps={{ translate: 'no', className: 'notranslate' }}
                          />
                        ),
                      })}
                      {showTableData({
                        ticket: 'Telephone number',
                        value: (
                          <TextField
                            {...addFormValues('phone')}
                            size="small"
                            helperText={errors.phone?.message}
                            error={!!errors.phone}
                            sx={{ width: '100%' }}
                            placeholder="03012345678"
                            inputProps={{ translate: 'no', className: 'notranslate' }}
                          />
                        ),
                        extraText: 'Please enter without hyphens ("-").',
                      })}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item>
                  <Button
                    onClick={() => { setIsEditing(false); }}
                    variant="outlined"
                    className="btn-outline-secondary"
                  >
                    Back
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    onClick={handleEditConfirmation}
                    className="btn-primary"
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </main>
    </div>
  );
}

export default ChangeForm;
